// frontend/src/components/Settings/SystemConfigAdmin.jsx
// Admin CRUD for core.menu_items and core.translations

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import Toast from '../Common/Toast';
import {
  FiPlus, FiEdit2, FiTrash2, FiSave, FiX,
  FiMenu, FiGlobe, FiSearch, FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import apiClient from '../../api/client'; // adjust import path if needed

// ── API helpers ───────────────────────────────────────────────────────────────
const systemAPI = {
  // Menu items
  getMenuItems:       ()        => apiClient.get('/system-config/menu-items'),
  createMenuItem:     (data)    => apiClient.post('/system-config/menu-items', data),
  updateMenuItem:     (id, data)=> apiClient.put(`/system-config/menu-items/${id}`, data),
  deleteMenuItem:     (id)      => apiClient.delete(`/system-config/menu-items/${id}`),
  // Translations
  getTranslations:    (params)  => apiClient.get('/system-config/translations', { params }),
  createTranslation:  (data)    => apiClient.post('/system-config/translations', data),
  updateTranslation:  (id, data)=> apiClient.put(`/system-config/translations/${id}`, data),
  deleteTranslation:  (id)      => apiClient.delete(`/system-config/translations/${id}`),
  upsertTranslations: (data)    => apiClient.post('/system-config/translations/bulk', data),
};

// ─────────────────────────────────────────────────────────────────────────────
// MENU ITEMS TAB
// ─────────────────────────────────────────────────────────────────────────────
const MENU_EMPTY = {
  menu_key: '', menu_sequence: '', description: '',
  parent_key: '', menu_level: 1, url: '', icon: '',
};

const MenuItemsTab = ({ toast_success, toast_error }) => {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(MENU_EMPTY);
  const [collapsed,  setCollapsed]  = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const r = await systemAPI.getMenuItems();
      if (r.data.success) setItems(r.data.data);
    } catch { toast_error('Erro ao carregar menu items'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.menu_key.trim()) { toast_error('menu_key é obrigatório'); return; }
    try {
      if (editId) {
        await systemAPI.updateMenuItem(editId, form);
        toast_success('Item atualizado');
      } else {
        await systemAPI.createMenuItem(form);
        toast_success('Item criado');
      }
      setShowForm(false); setEditId(null); setForm(MENU_EMPTY);
      await load();
    } catch (err) { toast_error(err.response?.data?.error || 'Erro ao guardar'); }
  };

  const handleEdit = (item) => {
    setForm({
      menu_key:     item.menu_key,
      menu_sequence:item.menu_sequence || '',
      description:  item.description  || '',
      parent_key:   item.parent_key   || '',
      menu_level:   item.menu_level   ?? 1,
      url:          item.url          || '',
      icon:         item.icon         || '',
    });
    setEditId(item.menu_items_id);
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar "${item.menu_key}"?`)) return;
    try {
      await systemAPI.deleteMenuItem(item.menu_items_id);
      toast_success('Item eliminado');
      await load();
    } catch (err) { toast_error(err.response?.data?.error || 'Erro ao eliminar'); }
  };

  const cancelForm = () => { setShowForm(false); setEditId(null); setForm(MENU_EMPTY); };

  // Build tree for display
  const rootItems = items
    .filter(i => !i.parent_key)
    .filter(i => !searchTerm || matchesSearch(i, searchTerm));
  const childrenOf = (key) => items
    .filter(i => i.parent_key === key)
    .filter(i => !searchTerm || matchesSearch(i, searchTerm));

  const allKeys = [...new Set(items.map(i => i.menu_key))];

  if (loading) return <div className="loading">A carregar…</div>;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'center', marginBottom:16, gap:12 }}>
        <div style={{ position:'relative', flex:'0 0 260px' }}>
          <FiSearch size={13} style={{ position:'absolute', left:9, top:'50%',
            transform:'translateY(-50%)', color:'var(--gray-400)' }}/>
          <input className="form-control" style={{ paddingLeft:30, height:34 }}
            placeholder="Pesquisar…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
        </div>
        <button className="btn btn-primary btn-sm"
          onClick={() => { setShowForm(true); setEditId(null); setForm(MENU_EMPTY); }}>
          <FiPlus size={13}/> Novo Item
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background:'#f0fdf9', border:'1px solid var(--gray-300)',
          borderRadius:8, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--gray-600)',
            marginBottom:12 }}>
            {editId ? 'Editar Item de Menu' : 'Novo Item de Menu'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">menu_key *</label>
              <input className="form-control" value={form.menu_key}
                onChange={e => setF('menu_key', e.target.value)}
                disabled={!!editId} placeholder="ex: my_page"/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Sequência</label>
              <input type="number" className="form-control" style={{ maxWidth:120 }}
                value={form.menu_sequence}
                onChange={e => setF('menu_sequence', e.target.value)}
                placeholder="ex: 20001"/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Descrição</label>
              <input className="form-control" value={form.description}
                onChange={e => setF('description', e.target.value)}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Parent Key</label>
              <input className="form-control" list="parent-keys-list"
                value={form.parent_key}
                onChange={e => setF('parent_key', e.target.value)}
                placeholder="Deixar vazio = raiz"/>
              <datalist id="parent-keys-list">
                {allKeys.map(k => <option key={k} value={k}/>)}
              </datalist>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Nível</label>
              <select className="form-control" value={form.menu_level}
                onChange={e => setF('menu_level', parseInt(e.target.value))}>
                <option value={0}>0 — Raiz</option>
                <option value={1}>1 — Filho</option>
                <option value={2}>2 — Neto</option>
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">URL</label>
              <input className="form-control" value={form.url}
                onChange={e => setF('url', e.target.value)}
                placeholder="/settings/my-page"/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Ícone (emoji)</label>
              <input className="form-control" value={form.icon}
                onChange={e => setF('icon', e.target.value)}
                placeholder="⚙️"/>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
            <button className="btn btn-secondary btn-sm" onClick={cancelForm}>
              <FiX size={12}/> Cancelar
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave}>
              <FiSave size={12}/> {editId ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </div>
      )}

      {/* Tree table */}
      <div className="form-section" style={{ padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--gray-50)',
              borderBottom:'1px solid var(--gray-200)' }}>
              {['','Chave','Seq.','Descrição','Parent','Nível','URL','Ícone','Ações'].map((h,i) => (
                <th key={i} style={{ padding:'8px 10px', textAlign:'left',
                  fontSize:11, fontWeight:700, color:'var(--gray-500)',
                  whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderTree(rootItems, childrenOf, collapsed, setCollapsed,
              handleEdit, handleDelete, 0)}
          </tbody>
        </table>
        {rootItems.length === 0 && (
          <div style={{ textAlign:'center', padding:32, color:'var(--gray-400)' }}>
            Sem itens encontrados.
          </div>
        )}
      </div>
    </div>
  );
};

function matchesSearch(item, term) {
  const t = term.toLowerCase();
  return item.menu_key.toLowerCase().includes(t)
    || (item.description || '').toLowerCase().includes(t)
    || (item.url || '').toLowerCase().includes(t);
}

function renderTree(nodes, childrenOf, collapsed, setCollapsed, onEdit, onDelete, depth) {
  return nodes
    .sort((a,b) => (a.menu_sequence||99999) - (b.menu_sequence||99999))
    .flatMap(item => {
      const children = childrenOf(item.menu_key);
      const hasChildren = children.length > 0;
      const isCollapsed = collapsed[item.menu_key];
      const rows = [(
        <tr key={item.menu_items_id}
          style={{ borderBottom:'1px solid var(--gray-100)',
            background: depth === 0 ? 'var(--gray-50)' : 'white' }}>
          <td style={{ padding:'7px 10px', width:24 }}>
            {hasChildren ? (
              <button type="button" onClick={() =>
                setCollapsed(c => ({ ...c, [item.menu_key]: !c[item.menu_key] }))}
                style={{ background:'none', border:'none', cursor:'pointer',
                  color:'var(--gray-400)', padding:0 }}>
                {isCollapsed ? <FiChevronRight size={13}/> : <FiChevronDown size={13}/>}
              </button>
            ) : null}
          </td>
          <td style={{ padding:'7px 10px',
            paddingLeft: 10 + depth * 18,
            fontWeight: depth === 0 ? 700 : 500 }}>
            <code style={{ background:'var(--gray-100)', padding:'1px 6px',
              borderRadius:4, fontSize:12 }}>
              {item.menu_key}
            </code>
          </td>
          <td style={{ padding:'7px 10px', color:'var(--gray-400)', fontSize:12 }}>
            {item.menu_sequence ?? '—'}
          </td>
          <td style={{ padding:'7px 10px' }}>{item.description || '—'}</td>
          <td style={{ padding:'7px 10px', color:'var(--gray-400)', fontSize:12 }}>
            {item.parent_key || '—'}
          </td>
          <td style={{ padding:'7px 10px', textAlign:'center' }}>
            <span style={{ fontSize:11, padding:'1px 6px', borderRadius:8,
              background:'var(--gray-100)', color:'var(--gray-500)', fontWeight:600 }}>
              {item.menu_level}
            </span>
          </td>
          <td style={{ padding:'7px 10px', fontSize:12, color:'var(--gray-500)',
            maxWidth:200, overflow:'hidden', textOverflow:'ellipsis',
            whiteSpace:'nowrap' }}>
            {item.url || '—'}
          </td>
          <td style={{ padding:'7px 10px', textAlign:'center', fontSize:16 }}>
            {item.icon || ''}
          </td>
          <td style={{ padding:'7px 10px' }}>
            <div style={{ display:'flex', gap:4 }}>
              <button className="btn-action btn-edit" title="Editar"
                onClick={() => onEdit(item)}><FiEdit2 size={13}/></button>
              <button className="btn-action btn-delete" title="Eliminar"
                onClick={() => onDelete(item)}><FiTrash2 size={13}/></button>
            </div>
          </td>
        </tr>
      )];
      if (hasChildren && !isCollapsed) {
        rows.push(...renderTree(children, childrenOf, collapsed, setCollapsed,
          onEdit, onDelete, depth + 1));
      }
      return rows;
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSLATIONS TAB
// ─────────────────────────────────────────────────────────────────────────────
const TRANS_EMPTY = {
  translation_key: '', language_code: 'pt', translation_text: '',
  page: '', category: '', description: '',
};

const TranslationsTab = ({ toast_success, toast_error }) => {
  const [items,      setItems]      = useState([]);
  const [meta,       setMeta]       = useState({ languages:[], pages:[], categories:[] });
  const [loading,    setLoading]    = useState(false);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(TRANS_EMPTY);
  const [filters,    setFilters]    = useState({ language_code:'', page:'', category:'', search:'' });
  // Bulk add: one key, text in all languages simultaneously
  const [showBulk,   setShowBulk]   = useState(false);
  const [bulkKey,    setBulkKey]    = useState('');
  const [bulkPage,   setBulkPage]   = useState('');
  const [bulkCat,    setBulkCat]    = useState('');
  const [bulkTexts,  setBulkTexts]  = useState({ pt:'', en:'' });

  const setF    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFilt = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.language_code) params.language_code = filters.language_code;
      if (filters.page)          params.page           = filters.page;
      if (filters.category)      params.category       = filters.category;
      if (filters.search)        params.search         = filters.search;
      const r = await systemAPI.getTranslations(params);
      if (r.data.success) {
        setItems(r.data.data);
        setMeta(r.data.meta || { languages:[], pages:[], categories:[] });
      }
    } catch { toast_error('Erro ao carregar traduções'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.translation_key || !form.language_code || !form.translation_text) {
      toast_error('Chave, idioma e texto são obrigatórios');
      return;
    }
    try {
      if (editId) {
        await systemAPI.updateTranslation(editId, form);
        toast_success('Tradução atualizada');
      } else {
        await systemAPI.createTranslation(form);
        toast_success('Tradução criada');
      }
      setShowForm(false); setEditId(null); setForm(TRANS_EMPTY);
      await load();
    } catch (err) { toast_error(err.response?.data?.error || 'Erro ao guardar'); }
  };

  const handleBulkSave = async () => {
    if (!bulkKey.trim()) { toast_error('translation_key é obrigatório'); return; }
    const translations = Object.entries(bulkTexts)
      .filter(([, text]) => text.trim())
      .map(([lang, text]) => ({
        translation_key:  bulkKey.trim(),
        language_code:    lang,
        translation_text: text.trim(),
        page:             bulkPage || null,
        category:         bulkCat  || null,
      }));
    if (!translations.length) { toast_error('Preencha pelo menos um idioma'); return; }
    try {
      const r = await systemAPI.upsertTranslations({ translations });
      toast_success(`${r.data.count} tradução(ões) guardada(s)`);
      setShowBulk(false); setBulkKey(''); setBulkTexts({ pt:'', en:'' });
      await load();
    } catch (err) { toast_error(err.response?.data?.error || 'Erro'); }
  };

  const handleEdit = (item) => {
    setForm({
      translation_key:  item.translation_key,
      language_code:    item.language_code,
      translation_text: item.translation_text,
      page:             item.page     || '',
      category:         item.category || '',
      description:      item.description || '',
    });
    setEditId(item.translation_id);
    setShowForm(true); setShowBulk(false);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar "${item.translation_key}" (${item.language_code})?`)) return;
    try {
      await systemAPI.deleteTranslation(item.translation_id);
      toast_success('Eliminado');
      await load();
    } catch (err) { toast_error(err.response?.data?.error || 'Erro'); }
  };

  const cancelForm = () => { setShowForm(false); setEditId(null); setForm(TRANS_EMPTY); };

  // Group items by translation_key for display
  const grouped = items.reduce((acc, row) => {
    if (!acc[row.translation_key]) acc[row.translation_key] = [];
    acc[row.translation_key].push(row);
    return acc;
  }, {});
  const keys = Object.keys(grouped).sort();

  return (
    <div>
      {/* Filters + toolbar */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap',
        alignItems:'flex-end' }}>
        <div style={{ position:'relative', flex:'0 0 240px' }}>
          <FiSearch size={13} style={{ position:'absolute', left:9, top:'50%',
            transform:'translateY(-50%)', color:'var(--gray-400)' }}/>
          <input className="form-control" style={{ paddingLeft:30, height:34 }}
            placeholder="Pesquisar chave ou texto…"
            value={filters.search}
            onChange={e => setFilt('search', e.target.value)}/>
        </div>
        <select className="form-control" style={{ width:130, height:34 }}
          value={filters.language_code}
          onChange={e => setFilt('language_code', e.target.value)}>
          <option value="">Todos idiomas</option>
          {meta.languages.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <select className="form-control" style={{ width:140, height:34 }}
          value={filters.page}
          onChange={e => setFilt('page', e.target.value)}>
          <option value="">Todas as páginas</option>
          {meta.pages.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="form-control" style={{ width:140, height:34 }}
          value={filters.category}
          onChange={e => setFilt('category', e.target.value)}>
          <option value="">Todas categorias</option>
          {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ fontSize:12, color:'var(--gray-400)', alignSelf:'center' }}>
          {items.length} linhas · {keys.length} chaves
        </span>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm"
            onClick={() => { setShowBulk(v => !v); setShowForm(false); setEditId(null); }}>
            <FiGlobe size={13}/> Adicionar por chave
          </button>
          <button className="btn btn-primary btn-sm"
            onClick={() => { setShowForm(true); setEditId(null);
              setForm(TRANS_EMPTY); setShowBulk(false); }}>
            <FiPlus size={13}/> Nova linha
          </button>
        </div>
      </div>

      {/* Bulk add (all languages at once) */}
      {showBulk && (
        <div style={{ background:'#f0fdf9', border:'1px solid var(--gray-300)',
          borderRadius:8, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--gray-600)',
            marginBottom:12 }}>
            <FiGlobe size={13}/> Adicionar chave em todos os idiomas
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10,
            marginBottom:10 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">translation_key *</label>
              <input className="form-control" value={bulkKey}
                onChange={e => setBulkKey(e.target.value)}
                placeholder="menu.my_key"/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Página</label>
              <input className="form-control" list="pages-list"
                value={bulkPage} onChange={e => setBulkPage(e.target.value)}/>
              <datalist id="pages-list">
                {meta.pages.map(p => <option key={p} value={p}/>)}
              </datalist>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Categoria</label>
              <input className="form-control" list="cats-list"
                value={bulkCat} onChange={e => setBulkCat(e.target.value)}/>
              <datalist id="cats-list">
                {meta.categories.map(c => <option key={c} value={c}/>)}
              </datalist>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10,
            marginBottom:12 }}>
            {Object.keys(bulkTexts).map(lang => (
              <div key={lang} className="form-group" style={{ margin:0 }}>
                <label className="form-label">
                  {lang.toUpperCase()}
                  {lang === 'pt' && ' (Português)'}
                  {lang === 'en' && ' (English)'}
                </label>
                <textarea className="form-control" rows={2}
                  value={bulkTexts[lang]}
                  onChange={e => setBulkTexts(t => ({ ...t, [lang]: e.target.value }))}
                  placeholder={`Texto em ${lang.toUpperCase()}…`}/>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary btn-sm"
              onClick={() => setShowBulk(false)}>
              <FiX size={12}/> Cancelar
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleBulkSave}>
              <FiSave size={12}/> Guardar todos os idiomas
            </button>
          </div>
        </div>
      )}

      {/* Single row form */}
      {showForm && (
        <div style={{ background:'#fffbeb', border:'1px solid var(--gray-300)',
          borderRadius:8, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--gray-600)',
            marginBottom:12 }}>
            {editId ? 'Editar Tradução' : 'Nova Tradução'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">translation_key *</label>
              <input className="form-control" value={form.translation_key}
                onChange={e => setF('translation_key', e.target.value)}
                disabled={!!editId}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Idioma *</label>
              <select className="form-control" value={form.language_code}
                onChange={e => setF('language_code', e.target.value)}
                disabled={!!editId}>
                <option value="pt">PT — Português</option>
                <option value="en">EN — English</option>
              </select>
            </div>
            <div className="form-group" style={{ margin:0, gridColumn:'1/-1' }}>
              <label className="form-label">Texto *</label>
              <textarea className="form-control" rows={2}
                value={form.translation_text}
                onChange={e => setF('translation_text', e.target.value)}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Página</label>
              <input className="form-control" list="pages-list2"
                value={form.page} onChange={e => setF('page', e.target.value)}/>
              <datalist id="pages-list2">
                {meta.pages.map(p => <option key={p} value={p}/>)}
              </datalist>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Categoria</label>
              <input className="form-control" list="cats-list2"
                value={form.category} onChange={e => setF('category', e.target.value)}/>
              <datalist id="cats-list2">
                {meta.categories.map(c => <option key={c} value={c}/>)}
              </datalist>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Descrição</label>
              <input className="form-control" value={form.description}
                onChange={e => setF('description', e.target.value)}/>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
            <button className="btn btn-secondary btn-sm" onClick={cancelForm}>
              <FiX size={12}/> Cancelar
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave}>
              <FiSave size={12}/> {editId ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </div>
      )}

      {/* Table grouped by key */}
      {loading ? (
        <div style={{ textAlign:'center', padding:32, color:'var(--gray-400)' }}>
          A carregar…
        </div>
      ) : (
        <div className="form-section" style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'var(--gray-50)',
                borderBottom:'1px solid var(--gray-200)' }}>
                {['Chave','Idioma','Texto','Página','Categoria','Ações'].map((h,i) => (
                  <th key={i} style={{ padding:'8px 10px', textAlign:'left',
                    fontSize:11, fontWeight:700, color:'var(--gray-500)',
                    whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map(key => grouped[key].map((row, ri) => (
                <tr key={row.translation_id}
                  style={{ borderBottom:'1px solid var(--gray-100)',
                    background: ri === 0 ? 'var(--gray-50)' : 'white' }}>
                  <td style={{ padding:'7px 10px', fontWeight: ri===0 ? 600 : 400 }}>
                    {ri === 0 ? (
                      <code style={{ background:'var(--gray-100)', padding:'1px 6px',
                        borderRadius:4, fontSize:12 }}>{key}</code>
                    ) : (
                      <span style={{ color:'var(--gray-300)', fontSize:11,
                        paddingLeft:8 }}>↳</span>
                    )}
                  </td>
                  <td style={{ padding:'7px 10px' }}>
                    <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11,
                      fontWeight:700,
                      background: row.language_code==='pt' ? '#dbeafe':'#f3e8ff',
                      color: row.language_code==='pt' ? '#1d4ed8':'#7c3aed' }}>
                      {row.language_code.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding:'7px 10px', maxWidth:340,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {row.translation_text}
                  </td>
                  <td style={{ padding:'7px 10px', fontSize:12,
                    color:'var(--gray-400)' }}>{row.page || '—'}</td>
                  <td style={{ padding:'7px 10px', fontSize:12,
                    color:'var(--gray-400)' }}>{row.category || '—'}</td>
                  <td style={{ padding:'7px 10px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <button className="btn-action btn-edit" title="Editar"
                        onClick={() => handleEdit(row)}><FiEdit2 size={13}/></button>
                      <button className="btn-action btn-delete" title="Eliminar"
                        onClick={() => handleDelete(row)}><FiTrash2 size={13}/></button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
          {keys.length === 0 && (
            <div style={{ textAlign:'center', padding:32, color:'var(--gray-400)' }}>
              Sem traduções encontradas.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT (tabbed)
// ─────────────────────────────────────────────────────────────────────────────
const SystemConfigAdmin = () => {
  const [tab, setTab] = useState('menu');
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const TABS = [
    { key:'menu',         label:<><FiMenu size={13}/> Menu Items</> },
    { key:'translations', label:<><FiGlobe size={13}/> Traduções</> },
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h2>Configuração do Sistema</h2>
          <p className="page-subtitle">
            Manutenção de itens de menu e traduções
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:'2px solid var(--gray-200)',
        marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.key} type="button"
            onClick={() => setTab(t.key)}
            style={{ padding:'8px 20px', background:'none', border:'none',
              borderBottom: tab===t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom:'-2px', cursor:'pointer', fontSize:13, fontWeight:600,
              color: tab===t.key ? 'var(--primary)' : 'var(--gray-500)',
              display:'flex', alignItems:'center', gap:6 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'menu' && (
        <MenuItemsTab toast_success={toast_success} toast_error={toast_error}/>
      )}
      {tab === 'translations' && (
        <TranslationsTab toast_success={toast_success} toast_error={toast_error}/>
      )}

      <div className="toast-container">
        {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast}/>)}
      </div>
    </div>
  );
};

export default SystemConfigAdmin;